/** Jersey legibility triage (port of jersey_grid.py's LEGIBILITY_PAGE,
 *  same address /labeling/jersey-legibility/).
 *
 * Click cycles unmarked → legible → not legible → unmarked; mark-all
 * shortcuts; submit sends {marks, shown} — unmarked shown tasks go
 * back to the queue server-side, exactly the Jinja contract.
 */
import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import './labeling.css'

type Task = { id: number; image: string }
type Mark = 'legible' | 'notlegible'
const ORDER: (Mark | undefined)[] = [undefined, 'legible', 'notlegible']

export default function JerseyLegibility() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [state, setState] = useState<Record<number, Mark>>({})
  const [status, setStatus] = useState('loading...')

  const loadBatch = useCallback(() => {
    setStatus('loading...')
    api<{ tasks?: Task[]; error?: string }>('/labeling/jersey-legibility/batch')
      .then((j) => {
        if (j.error) { setStatus(j.error); return }
        setTasks(j.tasks ?? []); setState({}); setStatus('')
      })
      .catch((e: Error) => setStatus(e.message))
  }, [])

  useEffect(() => { loadBatch() }, [loadBatch])

  const cycle = (id: number) => setState((prev) => {
    const next = ORDER[(ORDER.indexOf(prev[id]) + 1) % 3]
    const out = { ...prev }
    if (next === undefined) delete out[id]
    else out[id] = next
    return out
  })

  const markAll = (v: Mark) => setState(
    Object.fromEntries(tasks.map((t) => [t.id, v])))

  const submitMarked = async () => {
    setStatus('submitting...')
    try {
      const j = await api<{ error?: string }>('/labeling/jersey-legibility/submit', {
        method: 'POST',
        body: JSON.stringify({ marks: state, shown: tasks.map((t) => t.id) }),
      })
      if (j?.error) { setStatus(j.error); return }
      loadBatch()
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'submit failed')
    }
  }

  const v = Object.values(state)
  const stats = status ||
    `${tasks.length} shown · ${v.filter((x) => x === 'legible').length} legible · ` +
    `${v.filter((x) => x === 'notlegible').length} not legible · ${tasks.length - v.length} unmarked`

  return (
    <div className="lab-legibility">
      <div id="bar">
        {' '}<b>jersey legibility</b>{' '}
        <span className="k">click cycles: legible → not legible → clear</span>{' '}
        <button className="sec" onClick={() => markAll('legible')}>all legible</button>{' '}
        <button className="sec" onClick={() => markAll('notlegible')}>all not legible</button>{' '}
        <button onClick={submitMarked}>submit</button>{' '}
        <span id="stats">{stats}</span>
      </div>
      <div id="grid">
        {tasks.map((t) => (
          <div key={t.id} className={`cell ${state[t.id] || ''}`}
               onClick={() => cycle(t.id)}>
            <img loading="lazy" src={t.image} alt="" />
          </div>
        ))}
      </div>
    </div>
  )
}
